import type { AccessAction, ActionSpec } from './types/accessTypes';

/**
 * ACTION_REGISTRY - 单一注册表（文档 §3.7）
 *
 * 所有 AccessAction 的元数据真相源。新增 action 只需在此表加一行，
 * 前后端拦截逻辑自动生效。
 *
 * 维护规则：
 * 1. 新增 action 必须同时加测试用例到 test/access/actionRegistry.spec.ts
 * 2. capability 字段必须对应 plan_entitlements 中的列名（null 表示无 capability 要求）
 * 3. resourceType 非 null 时，accessPolicyService 会做资源事实校验（L4）
 * 4. partial=true 时，accessPolicyService 会调用 deriveMeteredBody 派生 25% 正文
 * 5. quota 非 null 时，accessPolicyService 会原子扣减配额
 * 6. ownerCheck=true 时，accessPolicyService 会校验 resource.ownerId === viewer.userId
 *
 * 路径偏离说明（reviewer Issue 2）：
 * spec §3.7 原名 src/domain/access/actionRegistry.ts，本计划改为 src/domain/accessRegistry.ts，
 * 因当前 src/domain/ 下无 access/ 子目录，新建子目录过度分层。
 *
 * 类型说明：
 * 使用 `satisfies Record<AccessAction, ActionSpec>` 保证完整性（所有 action 都有定义），
 * 但导出类型显式标注为 Record<AccessAction, ActionSpec>，否则 `as const` 会让每个条目
 * 收窄为字面量类型，导致 accessPolicyService 读取可选字段（ownerCheck/quota/partial/mediaKind）
 * 时 TS 报错 "Property does not exist"。
 */
const ACTION_REGISTRY_IMPL = {
	'topic.read':                 { capability: null, requiresSession: false, resourceType: null },
	'chapter.read':               { capability: null, requiresSession: false, resourceType: 'chapter' },
	'chapter.unlock':             { capability: null, requiresSession: false, resourceType: 'chapter', partial: true },
	'media.audio.play':           { capability: null, requiresSession: true, resourceType: 'chapter', mediaKind: 'audio' },
	'media.video.play':           { capability: null, requiresSession: true, resourceType: 'chapter', mediaKind: 'video' },
	'activity.favorite.write':    { capability: null, requiresSession: true },
	'activity.progress.write':    { capability: null, requiresSession: true },
	'activity.topicVote.write':   { capability: null, requiresSession: true },
	'community.idea.read':        { capability: null, requiresSession: false },
	'community.idea.submit':      { capability: 'canSubmitCommunityIdea', requiresSession: true },
	'community.idea.vote':        { capability: 'canVoteCommunityIdea', requiresSession: true },
	'community.comment.submit':   { capability: 'canCommentCommunityIdea', requiresSession: true },
	'github.summary.read':        { capability: null, requiresSession: false },
	'github.deepDive.open':       { capability: null, requiresSession: false, resourceType: 'chapter', partial: true, fullAccessCapability: 'githubDeepDive' },
	'ai.landing.read':            { capability: null, requiresSession: false },
	'ai.history.read':            { capability: null, requiresSession: true },
	'ai.job.read':                { capability: null, requiresSession: true, ownerCheck: true, resourceType: 'aiJob' },
	'ai.course.create':           { capability: null, requiresSession: true, quota: 'aiCreateBalance' },
	'privateCourse.read':         { capability: null, requiresSession: true, ownerCheck: true, resourceType: 'privateCourse' },
	'profile.preview.read':       { capability: null, requiresSession: false },
	'profile.dashboard.read':     { capability: null, requiresSession: true },
	'feedback.view':              { capability: null, requiresSession: true },
	'feedback.submit':            { capability: null, requiresSession: true },
	'feedback.reply':             { capability: null, requiresSession: true },
	'billing.manage':             { capability: null, requiresSession: true },
	'account.manage':             { capability: null, requiresSession: true },
	'upload.feedback':            { capability: null, requiresSession: true, uploadPurpose: 'feedback', requiresTurnstile: true },
	'upload.community':           { capability: 'canCommentCommunityIdea', requiresSession: true, uploadPurpose: 'community', requiresTurnstile: true },
	'upload.ai':                  { capability: null, requiresSession: true, quota: 'aiCreateBalance', uploadPurpose: 'ai', requiresTurnstile: true },
	'referral.center.read':       { capability: null, requiresSession: true },
	'referral.bind':              { capability: null, requiresSession: true },
	'notification.list':          { capability: null, requiresSession: true },
	'notification.read':          { capability: null, requiresSession: true },
	'notification.readAll':       { capability: null, requiresSession: true },
} as const satisfies Record<AccessAction, ActionSpec>;

export const ACTION_REGISTRY: Record<AccessAction, ActionSpec> = ACTION_REGISTRY_IMPL;

export type ActionRegistryKey = keyof typeof ACTION_REGISTRY_IMPL;
